/**
 * LandingPage — Phase 6 refonte
 *
 * 6.1  Hero avec pipeline visuel animé (Extension → IA → Adaptation → Score → Export)
 * 6.3  CTA double : "Créer mon CV" (→ /wizard) + "J'ai déjà un CV" (→ /wizard?mode=import)
 * 6.4  Compteur social via /api/metrics
 * 6.5  Capture e-mail landing → POST /api/landing/newsletter (Supabase `landing_newsletter_signups`)
 * 6.6  Section extension + lien Chrome Web Store (`landing.*`, `EXTENSION_WEB_STORE_URL`)
 * 6.7  « Comment ça marche » : 3 cartes i18n `landing.step1–3*` (CV → offre → analyse/export)
 *
 * Option "Ne plus afficher cette page" écrit cv_skip_landing dans localStorage.
 * Au mount : si cv_skip_landing est défini, redirige immédiatement vers /app.
 */
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Layers,
  Sparkles,
  FileInput,
  ExternalLink,
  ChevronRight,
  CheckCircle2,
  Globe,
  WandSparkles,
  ChartNoAxesCombined,
  Mail,
  ArrowRight,
  Puzzle,
  Zap,
  Cloud,
  Briefcase,
  Save,
} from "lucide-react";
import { EXTENSION_WEB_STORE_URL, EXTENSION_VERSION } from "./constants";
import { getT } from "./contexts/I18nContext";
import "./index.css";

const SKIP_LANDING_KEY = "cv_skip_landing";
const LOCALE_KEY = "cv_locale";

// ─── Pipeline steps built inside component so labels use t() ──────────────────

// ─── Component ────────────────────────────────────────────────────────────────

function LandingPage() {
  const navigate = useNavigate();
  const [locale, setLocale] = useState<"fr" | "en">(() =>
    (typeof window !== "undefined" && (localStorage.getItem(LOCALE_KEY) as "fr" | "en")) || "fr"
  );
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<{ totalRequests?: number } | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem(LOCALE_KEY, locale);
    document.documentElement.lang = locale;
  }, [locale]);

  // 6.1 — Pipeline animation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((s) => (s + 1) % PIPELINE_STEPS.length);
    }, 1800);
    return () => clearInterval(interval);
  }, []); // PIPELINE_STEPS.length is always 5 — static, no dep needed

  // 6.4 — Metrics
  useEffect(() => {
    fetch("/api/metrics")
      .then((r) => r.json())
      .then((d) => setMetrics(d))
      .catch(() => { /* ignore */ });
  }, []);

  const t = getT(locale);
  const toggleLocale = () => setLocale((prev) => (prev === "fr" ? "en" : "fr"));

  const PIPELINE_STEPS = [
    { icon: Briefcase,          label: t('landing2.pipelineExtension'),  sublabel: t('landing2.pipelineExtensionSub'),  color: "text-blue-400",    bg: "bg-blue-500/15 border-blue-500/25" },
    { icon: Zap,                label: t('landing2.pipelineAiAnalyze'),  sublabel: t('landing2.pipelineAiAnalyzeSub'),  color: "text-indigo-400",  bg: "bg-indigo-500/15 border-indigo-500/25" },
    { icon: WandSparkles,       label: t('landing2.pipelineAdapt'),      sublabel: t('landing2.pipelineAdaptSub'),      color: "text-violet-400",  bg: "bg-violet-500/15 border-violet-500/25" },
    { icon: ChartNoAxesCombined, label: t('landing2.pipelineScore'),      sublabel: t('landing2.pipelineScoreSub'),      color: "text-emerald-400", bg: "bg-emerald-500/15 border-emerald-500/25" },
    { icon: Save,               label: t('landing2.pipelineExport'),     sublabel: t('landing2.pipelineExportSub'),     color: "text-orange-400",  bg: "bg-orange-500/15 border-orange-500/25" },
  ];

  useEffect(() => {
    if (typeof window !== "undefined" && localStorage.getItem(SKIP_LANDING_KEY) === "1") {
      navigate("/app", { replace: true });
    }
  }, [navigate]);

  const handleSkipLanding = () => {
    localStorage.setItem(SKIP_LANDING_KEY, "1");
    navigate("/app", { replace: true });
  };

  // 6.5 — Newsletter waitlist (server + DB)
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || emailSubmitting) return;
    setEmailError(null);
    setEmailSubmitting(true);
    try {
      const res = await fetch("/api/landing/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), locale }),
      });
      if (!res.ok) {
        if (res.status === 429) setEmailError(t("landing2.newsletterRateLimit"));
        else if (res.status === 503) setEmailError(t("landing2.newsletterUnavailable"));
        else if (res.status === 400) setEmailError(t("landing2.newsletterInvalidEmail"));
        else setEmailError(t("landing2.newsletterError"));
        return;
      }
      setEmailSaved(true);
    } catch {
      setEmailError(t("landing2.newsletterError"));
    } finally {
      setEmailSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen overflow-y-auto overflow-x-hidden bg-[#0a0a0a] text-white font-sans antialiased">
      {/* ── Header ── */}
      <header className="sticky top-0 left-0 right-0 z-50 flex items-center justify-between px-4 md:px-6 py-3 bg-[#0a0a0a]/95 border-b border-white/10 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <div className="logo-crop w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-white/5">
            <img src="/logo-square-dark.png" alt="" className="w-full h-full object-cover" />
          </div>
          <span className="text-lg font-black tracking-tighter">ATSmyCV</span>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={toggleLocale}
            className="flex items-center gap-1.5 text-sm font-medium text-white/60 hover:text-white transition-colors"
            title={locale === "fr" ? "English" : "Français"}
          >
            <Globe size={16} />
            <span>{locale === "fr" ? "EN" : "FR"}</span>
          </button>
          <Link
            to="/app"
            className="text-sm font-semibold text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
          >
            {t("landing.accessEditor")} <ExternalLink size={13} />
          </Link>
        </div>
      </header>

      <main>
        {/* ── Hero — 6.1 Pipeline visuel ── */}
        <section className="px-4 md:px-6 pt-16 pb-12 md:pt-24 md:pb-16 max-w-5xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs font-semibold mb-6">
              <Zap className="size-3" />
              {t('landing2.optimizedForAts')}
            </div>
            <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-5 leading-tight">
              {t("landing.heroTitle")}
            </h1>
            <p className="text-lg md:text-xl text-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t("landing.heroSub")}
            </p>

            {/* 6.3 — Double CTA — always inline */}
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => navigate("/wizard")}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-4 sm:px-6 py-3 sm:py-3.5 rounded-xl text-sm sm:text-base transition-all shadow-lg shadow-blue-600/25"
              >
                <Zap size={16} className="sm:size-[18px]" />
                {t('landing2.createMyCv')}
              </button>
              <button
                type="button"
                onClick={() => navigate("/wizard?mode=import")}
                className="flex items-center gap-2 border border-white/20 bg-white/5 hover:bg-white/10 text-white/80 hover:text-white font-semibold px-4 sm:px-6 py-3 sm:py-3.5 rounded-xl text-sm sm:text-base transition-all"
              >
                <FileInput size={16} className="sm:size-[18px]" />
                {t('landing2.iAlreadyHaveCv')}
              </button>
            </div>

            {/* 6.4 — Social proof */}
            {metrics?.totalRequests !== undefined && metrics.totalRequests > 100 && (
              <p className="text-xs text-white/30 mt-5">
                {t('landing2.aiAnalysisCount').replace('{n}', metrics.totalRequests.toLocaleString(locale === 'fr' ? 'fr-FR' : 'en-US'))}
              </p>
            )}
          </div>

          {/* 6.1 — Pipeline steps visual — fits 5 on all screens */}
          <div className="relative">
            <div className="flex items-center justify-center gap-0.5 md:gap-0">
              {PIPELINE_STEPS.map((step, i) => {
                const Icon = step.icon;
                const isActive = activeStep === i;
                return (
                  <div key={step.label} className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setActiveStep(i)}
                      className={`
                        flex flex-col items-center gap-1.5 md:gap-2 px-3 sm:px-4 md:px-6 py-2.5 md:py-5 rounded-xl border transition-all duration-500 text-center
                        ${isActive ? `${step.bg} ${step.color} scale-105 shadow-lg` : 'border-white/10 text-white/30 hover:text-white/50 hover:border-white/20 bg-white/[0.01]'}
                      `}
                    >
                      <Icon className={`size-5 md:size-7 transition-transform duration-500 ${isActive ? 'scale-125' : ''}`} />
                      <span className="text-[9px] sm:text-[10px] md:text-sm font-bold leading-none">{step.label}</span>
                      {isActive && (
                        <span className="text-[8px] md:text-xs font-medium opacity-75 leading-snug max-w-[70px] md:max-w-none">{step.sublabel}</span>
                      )}
                    </button>
                    {i < PIPELINE_STEPS.length - 1 && (
                      <ArrowRight className={`hidden md:block size-4 mx-2 shrink-0 transition-colors ${activeStep > i ? 'text-blue-400' : 'text-white/15'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Features ── */}
        <section className="px-4 md:px-6 py-12 md:py-16 border-t border-white/10">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-2 text-center">
              {t("landing.allForAts")}
            </h2>
            <p className="text-center text-white/50 text-sm mb-8">{t('landing2.featuresSubtitle')}</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {[
                { icon: FileInput,            color: "text-sky-400",     bg: "bg-sky-600/20",     title: t('landing2.featureImportTitle'),      desc: t('landing2.featureImportDesc') },
                { icon: Zap,                  color: "text-violet-400",  bg: "bg-violet-600/20",  title: t('landing2.featureWizardTitle'),       desc: t('landing2.featureWizardDesc') },
                { icon: Briefcase,            color: "text-indigo-400",  bg: "bg-indigo-600/20",  title: t('landing2.featureSaveOffersTitle'),   desc: t('landing2.featureSaveOffersDesc') },
                { icon: Cloud,                color: "text-teal-400",    bg: "bg-teal-600/20",    title: t('landing2.featureCloudSyncTitle'),    desc: t('landing2.featureCloudSyncDesc') },
                { icon: Layers,               color: "text-blue-400",    bg: "bg-blue-600/20",    title: t('landing2.featureVersionsTitle'),     desc: t('landing2.featureVersionsDesc') },
                { icon: ChartNoAxesCombined,  color: "text-emerald-400", bg: "bg-emerald-600/20", title: t('landing2.featureScoreTitle'),        desc: t('landing2.featureScoreDesc') },
                { icon: WandSparkles,         color: "text-amber-400",   bg: "bg-amber-600/20",   title: t('landing2.featureAiAdaptTitle'),      desc: t('landing2.featureAiAdaptDesc') },
                { icon: Mail,                 color: "text-rose-400",    bg: "bg-rose-600/20",    title: t('landing2.featureLetterTitle'),       desc: t('landing2.featureLetterDesc') },
              ].map(({ icon: Icon, color, bg, title, desc }) => (
                <div key={title} className="p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-white/20 hover:bg-white/[0.05] transition-all">
                  <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3 ${color}`}>
                    <Icon size={20} />
                  </div>
                  <h3 className="font-semibold text-white mb-1 text-sm">{title}</h3>
                  <p className="text-xs text-white/60 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section className="px-4 md:px-6 py-12 md:py-16 border-t border-white/10 bg-white/[0.015]">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">
              {t("landing.howItWorks")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { num: "1", title: t("landing.step1Title"), desc: t("landing.step1Desc"), color: "bg-blue-600" },
                { num: "2", title: t("landing.step2Title"), desc: t("landing.step2Desc"), color: "bg-indigo-600" },
                { num: "3", title: t("landing.step3Title"), desc: t("landing.step3Desc"), color: "bg-violet-600" },
              ].map(({ num, title, desc, color }) => (
                <div key={num} className="flex flex-col items-center md:items-start gap-3 text-center md:text-left">
                  <div className={`w-9 h-9 rounded-full ${color} flex items-center justify-center text-white font-bold text-sm shrink-0`}>
                    {num}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1 text-sm">{title}</h3>
                    <p className="text-xs text-white/60 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Extension section — 6.6 ── */}
        <section className="px-4 md:px-6 py-12 md:py-16 border-t border-white/10" id="extension">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-10">
              {/* Text */}
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-semibold mb-4">
                  <Puzzle className="size-3" /> {t('landing2.extensionChromeLabel')}
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">
                  {t('landing2.sendOfferInOneClick')}
                </h2>
                <p className="text-white/60 text-sm leading-relaxed mb-5">
                  {t("landing.extensionSub")}
                </p>
                <a
                  href={EXTENSION_WEB_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-all shadow-lg shadow-indigo-600/20"
                >
                  <ExternalLink size={16} />
                  {t("landing.downloadExtension").replace('{version}', EXTENSION_VERSION)}
                </a>
                <p className="text-xs text-white/30 mt-3">{t("landing.extCompat")}</p>
                <ol className="mt-4 text-xs text-white/50 list-decimal list-inside space-y-1.5">
                  <li>{t("landing.extStep1")}</li>
                  <li>{t("landing.extStep2")}</li>
                  <li>{t("landing.extStep3")}</li>
                </ol>
              </div>

              {/* Visual mockup */}
              <div className="flex-1 md:max-w-xs">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/60" />
                    <div className="w-3 h-3 rounded-full bg-amber-500/60" />
                    <div className="w-3 h-3 rounded-full bg-emerald-500/60" />
                    <span className="text-xs text-white/30 ml-2">linkedin.com/jobs/…</span>
                  </div>
                  <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-3 space-y-2">
                    <p className="text-xs font-bold text-indigo-300 flex items-center gap-1.5">
                      <Sparkles className="size-3.5" /> ATSmyCV
                    </p>
                    <p className="text-[11px] text-white/70 font-medium">Développeur Frontend React</p>
                    <p className="text-[10px] text-white/40">Décathlon · Paris · CDI</p>
                    <div className="flex gap-2 mt-2">
                      <button type="button" className="flex-1 py-1.5 rounded-lg bg-indigo-600 text-[10px] font-bold text-white">
                        Envoyer + Analyser
                      </button>
                      <button type="button" className="px-2 py-1.5 rounded-lg bg-white/10 text-[10px] text-white/60">
                        ★
                      </button>
                    </div>
                  </div>
                  <p className="text-[10px] text-white/30 text-center">{t('landing2.popupExtensionLabel')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Email capture — 6.5 ── */}
        <section className="px-4 md:px-6 py-12 md:py-16 border-t border-white/10 bg-gradient-to-b from-blue-950/20 to-transparent">
          <div className="max-w-lg mx-auto text-center">
            <h2 className="text-xl font-bold text-white mb-2">{t('landing2.stayInformedTitle')}</h2>
            <p className="text-sm text-white/50 mb-2">{t('landing2.stayInformedSub')}</p>
            <p className="text-xs text-white/35 mb-6 max-w-md mx-auto leading-relaxed">{t('landing2.stayInformedHint')}</p>
            {emailSaved ? (
              <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm font-semibold">
                <CheckCircle2 className="size-5" />
                {t('landing2.emailSaved')}
              </div>
            ) : (
              <form onSubmit={handleEmailSubmit} className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError(null);
                    }}
                    disabled={emailSubmitting}
                    placeholder={locale === "fr" ? "vous@exemple.fr" : "you@example.com"}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-blue-500/50 disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={emailSubmitting}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:pointer-events-none text-white font-semibold text-sm transition-all"
                  >
                    <ArrowRight size={16} />
                  </button>
                </div>
                {emailError && (
                  <p className="text-xs text-red-400/90 text-left" role="alert">
                    {emailError}
                  </p>
                )}
              </form>
            )}
          </div>
        </section>

        {/* ── Final CTA ── */}
        <section className="px-4 md:px-6 py-16 md:py-20 border-t border-white/10 bg-white/[0.015]">
          <div className="max-w-xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-3">{t("landing.readyCta")}</h2>
            <p className="text-white/50 text-sm mb-8">{t('landing2.finalCtaSub')}</p>
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => navigate("/wizard")}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-4 sm:px-6 py-3 sm:py-3.5 rounded-xl text-sm sm:text-base transition-all shadow-lg shadow-blue-600/20"
              >
                <Zap size={16} /> {t('landing2.createMyCv')} <ChevronRight size={14} />
              </button>
              <button
                type="button"
                onClick={() => navigate("/wizard?mode=import")}
                className="flex items-center gap-2 border border-white/20 bg-white/5 hover:bg-white/10 text-white/80 font-semibold px-4 sm:px-6 py-3 sm:py-3.5 rounded-xl text-sm sm:text-base transition-all"
              >
                <FileInput size={16} /> {t('landing2.iAlreadyHaveCv')}
              </button>
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="px-4 md:px-6 py-6 border-t border-white/10">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/40">
            <span>{t("landing.footer")}</span>
            <div className="flex items-center gap-4">
              <Link to="/app" className="hover:text-white/60 transition-colors">{t('landing2.footerEditor')}</Link>
              <a href="#extension" className="hover:text-white/60 transition-colors">{t('landing2.footerExtension')}</a>
              <button
                type="button"
                onClick={handleSkipLanding}
                className="hover:text-white/60 underline underline-offset-2 transition-colors"
              >
                {t("landing.skipLanding")}
              </button>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}

export { LandingPage };
